import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';

// La detección del subdominio de una tienda de Shopify a partir de lo que tipea la agencia: el subdominio
// (`xyz`), el dominio `myshopify` (`xyz.myshopify.com`, con o sin `https://`) o la URL pública de la tienda
// (`www.tutienda.com`, que se resuelve con `shopify/detect-subdomain/`). Vivía adentro de `AddStoreForm`; la
// comparten el alta por OAuth y el pedido del link de instalación (`ShopifyLinkRequest`). Los textos de los
// errores son los de siempre (deuda: están en español fijo, no pasan por i18n).

/** El subdominio si el texto ya lo trae; `null` si es una URL pública que hay que detectar. */
export function extractSubdomainFromInput(input) {
    if (!input || !input.trim()) {
        return null;
    }

    const trimmed = input.trim();

    // Caso 1: URL completa myshopify.com (ej: xyz.myshopify.com o https://xyz.myshopify.com)
    const myshopifyMatch = trimmed.match(/(?:https?:\/\/)?([a-zA-Z0-9][a-zA-Z0-9-]*)\.myshopify\.com/);
    if (myshopifyMatch) {
        return myshopifyMatch[1];
    }

    // Caso 2: Solo subdominio (ej: xyz)
    // Validar que sea un subdominio válido (solo letras, números y guiones, empezando con letra o número)
    const subdomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*$/;
    if (subdomainRegex.test(trimmed) && !trimmed.includes('.')) {
        return trimmed;
    }

    // Caso 3: URL pública (ej: www.zapatillas.com.ar)
    // Retornar null para indicar que necesita detección automática
    return null;
}

/** Detecta el subdominio de una URL pública: primero el backend y, si no, leyendo la página. Rechaza con un Error. */
export async function detectSubdomainFromPublicUrl(publicUrl) {
    // Asegurar que la URL tenga protocolo
    let urlToFetch = publicUrl.trim();
    if (!urlToFetch.startsWith('http://') && !urlToFetch.startsWith('https://')) {
        urlToFetch = 'https://' + urlToFetch;
    }

    // Primero intentar usar endpoint backend (si existe)
    try {
        const backendResponse = await new APIClient(API_URL + 'shopify/detect-subdomain/').call('POST', {
            url: urlToFetch
        });

        if (backendResponse && backendResponse.status === 'OK' && backendResponse.data && backendResponse.data.subdomain) {
            return backendResponse.data.subdomain;
        }
    } catch (backendError) {
        // Si el endpoint no existe o falla, continuar con el método directo
        console.log('Backend endpoint no disponible, intentando detección directa...');
    }

    // Método directo desde el navegador (puede fallar por CORS)
    try {
        let response;
        let html;

        try {
            response = await fetch(urlToFetch, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    'Accept': 'text/html'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            html = await response.text();
        } catch (fetchError) {
            // Detectar si es error de CORS
            const isCorsError = fetchError.name === 'TypeError' ||
                               fetchError.message.includes('CORS') ||
                               fetchError.message.includes('Failed to fetch') ||
                               fetchError.message.includes('NetworkError');

            if (isCorsError) {
                throw new Error('No se puede acceder a la URL debido a restricciones de seguridad del navegador (CORS). Por favor, ingresa el subdominio directamente (ej: xyz.myshopify.com o solo xyz).');
            }
            throw new Error(`Error al acceder a la URL: ${fetchError.message}`);
        }

        // Buscar Shopify.shop en el HTML
        // Puede aparecer como: Shopify.shop = "tienda.myshopify.com" o var Shopify = { shop: "tienda.myshopify.com" }
        const shopifyShopPatterns = [
            /Shopify\.shop\s*=\s*["']([^"']+\.myshopify\.com)["']/,
            /Shopify\s*=\s*\{[^}]*shop\s*:\s*["']([^"']+\.myshopify\.com)["']/,
            /"shop"\s*:\s*["']([^"']+\.myshopify\.com)["']/,
            /window\.Shopify\s*=\s*\{[^}]*shop\s*:\s*["']([^"']+\.myshopify\.com)["']/,
            /Shopify\.shop\s*=\s*["']([^"']+)["']/,
            /shop\s*:\s*["']([^"']+\.myshopify\.com)["']/
        ];

        for (const pattern of shopifyShopPatterns) {
            const match = html.match(pattern);
            if (match && match[1]) {
                const fullDomain = match[1];
                const subdomainMatch = fullDomain.match(/([a-zA-Z0-9][a-zA-Z0-9-]*)\.myshopify\.com/);
                if (subdomainMatch) {
                    return subdomainMatch[1];
                }
            }
        }

        throw new Error('No se encontró información de Shopify en la página. Asegúrate de que sea una tienda de Shopify.');
    } catch (error) {
        if (error.message) {
            throw error;
        }
        throw new Error('Error al detectar el subdominio. Por favor, ingresa el subdominio directamente.');
    }
}

/** ¿El subdominio arma una URL `https://<sub>.myshopify.com` válida? (la validación del botón de OAuth). */
export function subdomainIsValid(subdomain) {
    if (!subdomain) {
        return false;
    }

    // Validate the subdomain by constructing the full URL and testing against the user's regex.
    const urlToValidate = `https://${subdomain}.myshopify.com`;
    const shopifyRegex = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com\/?/;

    return shopifyRegex.test(urlToValidate)
}

/**
 * El dominio `myshopify` que se manda al pedir el link (`mitienda.myshopify.com`, en minúscula), o `null` si
 * el texto todavía no lo trae (una URL pública sin detectar). Es más estricto que `subdomainIsValid`: ese
 * acepta `xyz.myshopify.com` tipeado como si fuera un subdominio y armaría `xyz.myshopify.com.myshopify.com`.
 */
export function shopDomainOf(input) {
    const subdomain = extractSubdomainFromInput(input);
    return subdomain ? `${subdomain.toLowerCase()}.myshopify.com` : null;
}
